'use client'

import { useState } from 'react'
import Link from 'next/link'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import ForceTheme from '@/components/ForceTheme'
import FreeCheckLauncher from '@/components/platform/FreeCheckLauncher'
import Section from '@/components/marketing/Section'
import Card from '@/components/marketing/Card'
import { InstrumentLabel } from '@/components/radar/ui/InstrumentLabel'
import { Button, LinkButton } from '@/components/ui/button'
import { openCheckout } from '@/lib/paddle/checkout'
import { PoweredByPaddle } from '@/components/PoweredByPaddle'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import {
  RADAR_ENTRY_PRICE,
  RADAR_FREE_TIER,
  RADAR_PACK_FEATURES,
  RADAR_PACK_OPTIONS,
  RADAR_RETAINER_TIER,
} from '@/lib/radar/pricing-tiers'
import {
  DEMO_DOMAIN,
  DEMO_ELAPSED,
  DEMO_GRADE,
  DEMO_HIGH_IMPACT_COUNT,
  DEMO_OPEN_ISSUES,
  DEMO_PASSING_COUNT,
  DEMO_PILLARS,
  DEMO_RANKED_ACTIONS,
  DEMO_SCORE,
  DEMO_TOOL_COUNT,
} from '@/lib/radar/demo-audit'
import type { AuditedDomainStat } from '@/lib/platform/audited-domain-count'
import { RadarLockup } from '@/components/brand/RadarMark'

/**
 * Radar landing page, rebuilt 2026-08-08 from the approved design in
 * public/"Radar by Pixelmojo — Find and fix what AI gets wrong.html",
 * adapted to the Pixelmojo design system (tokens, sharp corners, hairline
 * borders, instrument labels, the score gradient).
 *
 * Deliberate departures from the mock, per standing rules and Lloyd's
 * direction (2026-08-08):
 * - The domain form is <FreeCheckLauncher />, which carries the free-check
 *   gate and email verification. It renders ONLY in the closing #free-audit
 *   band, never in the hero: the launcher was pulled out of the hero on
 *   2026-08-08 at Lloyd's request because a returning visitor who had spent
 *   their free check saw its "You have used your free check" card as the
 *   hero. The 2026-09-04 heuristic evaluation (F-07) asked to move it back
 *   up; that was implemented and then reverted the same day at Lloyd's
 *   direction, for the same reason. Solve the spent-check hero state before
 *   proposing this again.
 * - The "950+ domains audited" stat renders the live RPC count passed
 *   down from the server component, and is omitted when unavailable —
 *   never a hardcoded number. It arrives with the date it was read, and
 *   both come from lib/platform/audited-domain-count, the one module every
 *   surface quoting a Radar domain total reads.
 * - The two audit artifacts on this page (the hero scan card and the sample
 *   dashboard) read the canonical demo dataset in lib/radar/demo-audit — the
 *   same one the homepage dashboard renders. Until 2026-09-04 they were
 *   hand-typed at C 56/100 over four pillars with Trust at 0, against the
 *   homepage's B 81/100 over five (heuristic finding F-04). Do not retype a
 *   score, grade, pillar, count or action title here. Import it.
 * - Pricing CTAs open real Paddle checkout via @/lib/paddle/checkout.
 * - The mock's own site header/footer are dropped; the global nav and
 *   footer wrap this page.
 */

const WORKFLOW = [
  {
    step: '01',
    title: 'Sweep the full AI visibility stack',
    copy: 'Run 13 checks in staged batches. Radar catches cross-tool conflicts that isolated tests miss.',
    tags: ['Crawl access', 'Schema', 'Citations', 'SOV', 'Hallucinations'],
  },
  {
    step: '02',
    title: 'See the issue in plain language',
    copy: 'Get a scored report, the evidence behind every finding, and a ranked action list, not another vague dashboard.',
    tags: ['Severity scoring', 'Evidence', 'Cross-tool insight'],
  },
  {
    step: '03',
    title: 'Implement a fix written for your domain',
    copy: 'Copy a context-rich prompt into Claude, ChatGPT, or Cursor. Use guided threads for crawlability, structured data, content authority, and citation building.',
    tags: [
      'AI prompt generator',
      'Implementation threads',
      'Ready-to-use markup',
    ],
  },
  {
    step: '04',
    title: 'Re-verify without re-running everything',
    copy: 'Fix a robots rule, schema block, or page, then retest that check in seconds. Track progress without paying for another full audit.',
    tags: ['Single-tool recheck', 'Saved progress', 'Competitor comparison'],
  },
]

const TOOLS: Array<{
  name: string
  desc: string
  tier: 'free' | 'paid'
}> = [
  {
    name: 'AI Crawl Check',
    desc: 'Tests AI and search bots for access to your site.',
    tier: 'free',
  },
  {
    name: 'Robots.txt Analyzer',
    desc: 'Deep-parses directives and conflicts across bot rules.',
    tier: 'free',
  },
  {
    name: 'llms.txt Validator',
    desc: 'Checks structure, links, sections, and entity signals.',
    tier: 'free',
  },
  {
    name: 'AI Readiness Score',
    desc: 'Summarizes visibility health across five categories.',
    tier: 'free',
  },
  {
    name: 'AEO Page Auditor',
    desc: 'Scores a page’s answer-engine readiness.',
    tier: 'free',
  },
  {
    name: 'Schema Audit',
    desc: 'Validates structured data across key schema types.',
    tier: 'free',
  },
  {
    name: 'Citation Tracker',
    desc: 'Queries ChatGPT, Claude, Gemini, and Perplexity.',
    tier: 'paid',
  },
  {
    name: 'Reddit Monitor',
    desc: 'Finds community mentions and suspicious seeded content.',
    tier: 'paid',
  },
  {
    name: 'Citation Tester',
    desc: 'Checks whether answer engines cite a specific page.',
    tier: 'paid',
  },
  {
    name: 'Source Influence',
    desc: 'Maps the domains shaping narratives in your category.',
    tier: 'paid',
  },
  {
    name: 'Prompt SOV',
    desc: 'Measures your share of AI recommendations.',
    tier: 'paid',
  },
  {
    name: 'Hallucination Check',
    desc: 'Flags factual errors in AI claims about your brand.',
    tier: 'paid',
  },
  {
    name: 'Brand Disambiguation',
    desc: 'Confirms AI is talking about the right company.',
    tier: 'paid',
  },
]

// The Radar ladder now lives in @/lib/radar/pricing-tiers, derived from
// @/lib/paddle/pricing (the checkout source of truth) and shared with
// /pricing, so the two commercial surfaces cannot drift (F-01, 2026-09-04).

const FAQS = [
  {
    q: 'What is included in the free audit?',
    a: 'One audit per domain across six technical readiness tools: AI bot crawl access, robots.txt, llms.txt, schema markup, AEO page readiness, and your overall AI Readiness score. No credit card is required.',
  },
  {
    q: 'What does a paid full audit add?',
    a: 'It unlocks all 13 tools, including live citation tracking, Reddit monitoring, hallucination detection, prompt share of voice, source influence, answer-engine citation testing, and brand disambiguation, plus fix threads, PDF export, history, and competitor comparison.',
  },
  {
    // Exact API model names are a claims-integrity requirement (the C6
    // claims audit), inherited from the retired /platform/pricing FAQ and
    // guarded by src/lib/radar/measured-models.test.ts.
    q: 'Which AI engines does Radar query?',
    a: 'Paid audits query the provider APIs for ChatGPT (OpenAI gpt-4o-mini), Claude (Anthropic Claude Haiku), Gemini (Google Gemini 2.5 Flash), and Perplexity (Sonar). Citation and share-of-voice results are a live snapshot of those API responses at audit time. Consumer apps like chatgpt.com may differ, since they can add different models, memory, personalization, and live web search. Full details on the methodology page.',
  },
  {
    q: 'Do audit credits expire?',
    a: 'No. Single and pack credits do not expire, so you can use them after a launch, after implementing fixes, or whenever you want to compare a competitor.',
  },
  {
    q: 'Can I verify one fix without a full re-audit?',
    a: 'Yes. Re-run an individual completed tool to verify a change, such as a robots.txt or schema fix, without running all 13 tools again.',
  },
  {
    q: 'Who is Pro for?',
    a: 'Pro is designed for agencies with active client retainers and in-house teams that manage ongoing AI visibility. It includes 40 full audits per month plus weekly pulse re-scans for watched domains.',
  },
]

/**
 * The hero scan card's pillar meters, read off the canonical demo audit
 * (`lib/radar/demo-audit`) — the same dataset the homepage dashboard renders.
 *
 * All FIVE pillars, in the product's own order, each coloured by its own
 * grade. The four hand-typed bars this replaced dropped Community entirely and
 * pinned Trust at 0 beside a hand-typed C 56/100, while the homepage showed
 * B 81/100 across five: two scorecards for one product, which is exactly the
 * contradiction heuristic finding F-04 named. Nothing on this page may restate
 * a number the dataset does not produce.
 *
 * A null pillar score means "no gradeable tool here", never a zero: the meter
 * stays empty and the readout is a dash, matching the dashboard's own rule.
 */
const HERO_BARS = DEMO_PILLARS.map(pillar => ({
  label: pillar.label,
  width: pillar.score ?? 0,
  value: pillar.score === null ? '--' : String(pillar.score),
  color: pillar.grade ? GRADE_COLORS[pillar.grade] : 'var(--muted-foreground)',
}))

/**
 * Severity chip for the sample dashboard's action rows, keyed by the
 * `ActionItem.impact` the canonical dataset carries. Presentation only — the
 * rows themselves come from `DEMO_RANKED_ACTIONS`, in the product's own order.
 */
const ACTION_IMPACT: Record<
  'high' | 'medium' | 'low',
  { label: string; color: string }
> = {
  high: { label: 'HIGH', color: 'text-cta' },
  medium: { label: 'MED', color: 'text-signal' },
  low: { label: 'LOW', color: 'text-growth' },
}

function Kicker({ children }: { children: React.ReactNode }) {
  return <InstrumentLabel className='mb-5'>{children}</InstrumentLabel>
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className='thesis-headline mb-5'>{children}</h2>
}

export default function PlatformLandingPage({
  auditedDomainStat,
}: {
  // THE canonical Radar domain count plus the date it was read, from
  // lib/platform/audited-domain-count — the same module the homepage and
  // /platform/start read, so no two pages can quote different totals.
  // A null count means the figure is unavailable and the proof stat is
  // omitted, never faked.
  auditedDomainStat?: AuditedDomainStat
}) {
  const [toolFilter, setToolFilter] = useState<'all' | 'free' | 'paid'>('all')
  const [pack, setPack] = useState(0)

  const visibleTools =
    toolFilter === 'all' ? TOOLS : TOOLS.filter(t => t.tier === toolFilter)

  return (
    <>
      <ForceTheme theme='dark' />
      {/* The ambient brand wash is global now (globals.css, body::before),
          so this page no longer paints its own. */}
      <div className='min-h-screen text-foreground'>
        {/* ── Hero: copy + launcher left, live-audit artifact right */}
        <Section width='hero' innerClassName='relative'>
          {/* The Arabic switcher used to live here as a bare anchor floating
              above the hero. It now lives in the site header via
              <LocaleSwitcher />, which reads lib/i18n/locale-alternates and so
              renders on every route that has a translation instead of this one
              (F-13, 2026-09-04). The plain-<a> requirement moved with it: a
              client transition would land on the target still carrying the
              previous document's lang/dir. */}
          <div className='grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center'>
            <LenisFadeIn revealOnScroll>
              <div>
                <InstrumentLabel className='mb-6'>
                  AI brand accuracy audit
                </InstrumentLabel>
                <h1 className='thesis-headline thesis-headline--category'>
                  AI is talking about your brand.{' '}
                  {/* Radar magenta — the same pink that carries the quadrant
                      in RadarMark, so the headline payoff and the product
                      mark read as one identity. */}
                  <span className='text-cta'>Radar fixes what it says.</span>
                </h1>
                <p className='text-lg text-muted-foreground leading-relaxed max-w-xl mt-6'>
                  See why ChatGPT, Claude, Gemini, and Perplexity misread, skip,
                  or wrongly describe your brand, then get the exact fix for
                  every issue.
                </p>

                {/* The hero carries no form (the audit form lives in the
                    final CTA), so this anchor is its one action: it names
                    the entry price and scrolls to the pricing band that
                    sits two sections down. It takes the primary treatment
                    despite being an in-page jump — nothing else competes
                    with it in the fold, and a quiet variant left the hero
                    reading as if it had no call to action at all.

                    F-07 of the 2026-09-04 heuristic evaluation asked for the
                    launcher to move up here. Reverted at Lloyd's direction on
                    2026-09-04: it re-creates the 2026-08-08 problem the
                    launcher was pulled out for — a returning visitor who has
                    spent their free check gets "You have used your free
                    check" as the hero. Do not re-litigate without solving
                    that state first. */}
                <LinkButton
                  href='#pricing'
                  variant='action'
                  size='lg'
                  className='mt-8 w-full sm:w-auto group'
                >
                  See pricing, from {RADAR_ENTRY_PRICE}
                  <span
                    className='transition-transform duration-200 group-hover:translate-y-[3px]'
                    aria-hidden='true'
                  >
                    ↓
                  </span>
                </LinkButton>
              </div>
            </LenisFadeIn>

            {/* Sample audit artifact per the mock: radar rings with a
                rotating sweep and blips, scan cards floating over the circle.
                Illustrative demonstration content on our own brand. Motion is
                motion-safe gated; reduced motion sees the settled frame. */}
            <LenisFadeIn delay={120} revealOnScroll>
              <div
                className='relative min-h-[420px] sm:min-h-[560px] grid place-items-center'
                aria-label='Sample Radar audit visualization'
              >
                {/* Rings, crosshair, sweep, blips */}
                <div
                  className='force-circle relative w-[min(100%,520px)] aspect-square rounded-full border border-line overflow-hidden'
                  aria-hidden='true'
                  style={{
                    background: [
                      'linear-gradient(90deg, transparent calc(50% - 0.5px), color-mix(in srgb, var(--border) 45%, transparent) 50%, transparent calc(50% + 0.5px))',
                      'linear-gradient(transparent calc(50% - 0.5px), color-mix(in srgb, var(--border) 45%, transparent) 50%, transparent calc(50% + 0.5px))',
                      'repeating-radial-gradient(circle, transparent 0 18%, color-mix(in srgb, var(--border) 45%, transparent) 18.2% 18.6%, transparent 18.8% 36%)',
                    ].join(', '),
                  }}
                >
                  <div
                    className='force-circle absolute -inset-px rounded-full motion-safe:animate-[spin_7s_linear_infinite]'
                    style={{
                      background:
                        'conic-gradient(from 0deg, transparent 0 72%, color-mix(in srgb, var(--signal) 6%, transparent) 78%, color-mix(in srgb, var(--signal) 38%, transparent) 98%, transparent)',
                    }}
                  />
                  <span className='force-circle absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-signal' />
                  <span className='force-circle absolute top-[25%] left-[61%] w-2 h-2 rounded-full bg-signal motion-safe:animate-pulse' />
                  <span className='force-circle absolute top-[63%] left-[29%] w-2 h-2 rounded-full bg-accent motion-safe:animate-pulse' />
                  <span className='force-circle absolute top-[72%] left-[68%] w-2 h-2 rounded-full bg-cta motion-safe:animate-pulse' />
                </div>

                {/* Scan card: readiness score */}
                <div className='absolute top-[4%] right-0 w-[min(300px,72vw)] border border-line bg-background/85 backdrop-blur-md p-4'>
                  <div className='flex items-center justify-between gap-4 pb-3 border-b border-line'>
                    <span className='instrument-label text-muted-foreground'>
                      AI readiness
                    </span>
                    {/* Was "Live audit". This card has always drawn fixed
                        sample figures, so the badge claimed something the
                        artifact could not deliver — and the homepage's own
                        dashboard says "fixed sample data" two clicks away.
                        One disclosure, both surfaces. */}
                    <span className='instrument-label text-muted-foreground'>
                      Sample data
                    </span>
                  </div>
                  <div className='flex items-baseline gap-2.5 py-4'>
                    <span
                      className='font-display font-bold text-4xl leading-none'
                      style={{ color: GRADE_COLORS[DEMO_GRADE] }}
                    >
                      {DEMO_GRADE}
                    </span>
                    <span className='font-display font-bold text-3xl leading-none text-foreground'>
                      {DEMO_SCORE}
                    </span>
                    <span className='text-xs text-muted-foreground'>/ 100</span>
                  </div>
                  <div className='flex flex-col gap-2.5'>
                    {HERO_BARS.map(bar => (
                      <div
                        key={bar.label}
                        className='grid grid-cols-[5rem_1fr_1.5rem] items-center gap-2.5'
                      >
                        <span className='text-xs text-muted-foreground'>
                          {bar.label}
                        </span>
                        <span className='h-1 bg-muted/60 overflow-hidden'>
                          <span
                            className='block h-full'
                            style={{
                              width: `${bar.width}%`,
                              background: bar.color,
                            }}
                          />
                        </span>
                        <span className='text-xs font-mono text-foreground/80 text-right'>
                          {bar.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scan card: priority finding */}
                <div className='absolute bottom-[2%] left-0 w-[min(300px,72vw)] border border-line bg-background/85 backdrop-blur-md p-4'>
                  <div className='flex items-center justify-between gap-4 pb-3 border-b border-line'>
                    <span className='instrument-label text-muted-foreground'>
                      Priority finding
                    </span>
                    <span className='instrument-label text-muted-foreground'>
                      1 of {DEMO_OPEN_ISSUES}
                    </span>
                  </div>
                  <div className='flex items-start gap-3 pt-3'>
                    <span
                      className='flex items-center justify-center w-6 h-6 shrink-0 font-mono text-sm font-bold text-cta border border-cta'
                      aria-hidden='true'
                    >
                      !
                    </span>
                    <div>
                      <p className='text-sm font-semibold text-foreground m-0'>
                        AI is inferring your brand story
                      </p>
                      <p className='text-xs text-muted-foreground leading-relaxed m-0 mt-1'>
                        Missing entity signals are causing conflicting
                        descriptions across answer engines.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </LenisFadeIn>
          </div>
        </Section>

        {/* ── Proof strip. Same construction as the /about stat strip so the
            two pages read as one system: tinted band, 1.2fr intro + three
            equal cells, hairline dividers between cells, display numerals
            over instrument-label captions. */}
        <div className='border-y border-line bg-card/40'>
          <Section width='wide' rhythm='none'>
            <div className='grid md:grid-cols-[1.2fr_repeat(3,1fr)]'>
              <div className='py-6 md:py-8 md:pr-8 flex flex-col justify-center'>
                {/* Product nameplate: the global header carries the Pixelmojo
                    logo, so the lockup supplies the product identity here. */}
                <RadarLockup size='md' />
                <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-7'>
                  Built from a real six-month journey from AI invisibility to
                  consistent citations.
                </p>
              </div>
              {typeof auditedDomainStat?.count === 'number' &&
                auditedDomainStat.count > 0 && (
                  <div className='py-6 md:py-8 md:px-8 border-t md:border-t-0 md:border-l border-line flex flex-col justify-center'>
                    <div className='font-display font-bold text-4xl leading-none tracking-display-lg text-foreground'>
                      {/* Locale pinned: this is a Client Component, so a
                          bare toLocaleString() formats with Node's locale on
                          the server and the browser's on hydration (de-DE
                          renders 1.100 against the server's 1,100). */}
                      {auditedDomainStat.count.toLocaleString('en-US')}+
                    </div>
                    {/* The noun is load-bearing: this counts DISTINCT domains,
                        not audits run, and the two differ by roughly 5x. The
                        as-of date sits with it so a reader can tell a live
                        figure from a number typed into the page once. */}
                    <p className='instrument-label text-muted-foreground m-0 mt-2'>
                      Unique domains audited
                    </p>
                    <p className='text-2xs text-muted-foreground m-0 mt-1.5'>
                      <time dateTime={auditedDomainStat.asOf}>
                        as of {auditedDomainStat.asOfLabel}
                      </time>
                    </p>
                  </div>
                )}
              <div className='py-6 md:py-8 md:px-8 border-t md:border-t-0 md:border-l border-line flex flex-col justify-center'>
                <div className='font-display font-bold text-4xl leading-none tracking-display-lg text-foreground'>
                  0/4 → 4/4
                </div>
                <p className='instrument-label text-muted-foreground m-0 mt-2'>
                  LLMs citing Pixelmojo
                </p>
              </div>
              <div className='py-6 md:py-8 md:px-8 border-t md:border-t-0 md:border-l border-line flex flex-col justify-center'>
                <div className='font-display font-bold text-4xl leading-none tracking-display-lg text-foreground'>
                  516
                </div>
                <p className='instrument-label text-muted-foreground m-0 mt-2'>
                  Commits behind the proof
                </p>
              </div>
            </div>
          </Section>
        </div>

        {/* ── Pricing (mock: hairline top rule + a soft signal wash) */}
        <div
          className='border-t border-line'
          style={{
            backgroundImage:
              'linear-gradient(180deg, transparent, color-mix(in srgb, var(--signal) 4%, transparent))',
          }}
        >
          <Section width='wide' id='pricing'>
            <LenisFadeIn revealOnScroll>
              <div className='grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-16 items-end mb-12'>
                <div>
                  <Kicker>Simple pricing</Kicker>
                  <SectionHeading>
                    Start free. Unlock only when the answer matters.
                  </SectionHeading>
                </div>
                <p className='text-muted-foreground leading-relaxed m-0 max-w-md'>
                  No annual contract. Audit credits never expire. Move to weekly
                  monitoring when AI visibility becomes an ongoing
                  responsibility.
                </p>
              </div>
            </LenisFadeIn>

            {/* One `.price-card` spec drives all three tiers (globals.css)
                so padding, price scale, description floor, CTA margins, and
                feature rhythm cannot drift between them. */}
            <div className='grid md:grid-cols-3 gap-5 items-stretch'>
              <LenisFadeIn revealOnScroll className='h-full'>
                <div className='price-card'>
                  <p className='price-plan'>{RADAR_FREE_TIER.plan}</p>
                  <p className='price-amount'>
                    <strong>{RADAR_FREE_TIER.price}</strong>
                    <span>{RADAR_FREE_TIER.unit}</span>
                  </p>
                  <p className='price-note'>{RADAR_FREE_TIER.note}</p>
                  {/* Scrolls to the final CTA band, which holds the only
                      instance of the form. */}
                  <LinkButton
                    href='#free-audit'
                    variant='neutral'
                    className='price-cta group'
                  >
                    Get My Free Snapshot
                    <span
                      className='transition-transform duration-200 group-hover:translate-y-[3px]'
                      aria-hidden='true'
                    >
                      ↓
                    </span>
                  </LinkButton>
                  <ul className='price-features'>
                    {RADAR_FREE_TIER.features.map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </LenisFadeIn>

              <LenisFadeIn delay={80} revealOnScroll className='h-full'>
                <div className='price-card price-card--featured'>
                  <span className='force-circle absolute top-4 right-4 bg-[var(--cta-fill)] text-white text-[9px] font-extrabold uppercase tracking-kicker px-2.5 py-1.5'>
                    Best value
                  </span>
                  <p className='price-plan'>Audit packs</p>
                  <p className='price-amount'>
                    <strong>{RADAR_PACK_OPTIONS[pack].price}</strong>
                    <span>{RADAR_PACK_OPTIONS[pack].unit}</span>
                  </p>
                  <p className='price-note'>{RADAR_PACK_OPTIONS[pack].desc}</p>
                  {/* Pack selector. The old version marked the active
                      segment with bg-muted inside a bg-background shell —
                      two dark neutrals, so the selection was invisible.
                      Now each option is its own tile: the selected one
                      takes the CTA border, a tinted fill, and a check,
                      and the caption underneath restates the choice in
                      per-audit terms so the state is legible three ways. */}
                  <p className='instrument-label text-muted-foreground m-0 mt-8 mb-3'>
                    How many audits?
                  </p>
                  <div
                    className='grid grid-cols-3 gap-2'
                    role='group'
                    aria-label='Choose audit pack'
                  >
                    {RADAR_PACK_OPTIONS.map((opt, i) => {
                      const selected = pack === i
                      return (
                        <button
                          key={opt.label}
                          type='button'
                          aria-pressed={selected}
                          onClick={() => setPack(i)}
                          className={`relative flex flex-col items-center gap-0.5 border py-2.5 px-1 transition-colors ${
                            selected
                              ? 'border-cta bg-cta/10 text-foreground'
                              : 'border-line-strong text-muted-foreground hover:border-border hover:text-foreground'
                          }`}
                        >
                          {selected && (
                            <span
                              className='absolute top-1 right-1.5 text-2xs text-cta font-bold leading-none'
                              aria-hidden='true'
                            >
                              ✓
                            </span>
                          )}
                          <span className='font-display font-bold text-lg leading-none'>
                            {opt.count}
                          </span>
                          <span className='text-2xs uppercase tracking-kicker'>
                            {opt.count === 1 ? 'audit' : 'audits'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                  <p className='text-xs text-muted-foreground m-0 mt-3'>
                    {RADAR_PACK_OPTIONS[pack].perAudit} per audit
                    {RADAR_PACK_OPTIONS[pack].save && (
                      <>
                        {' · '}
                        <span className='text-growth font-semibold'>
                          {RADAR_PACK_OPTIONS[pack].save}
                        </span>
                      </>
                    )}
                  </p>
                  <Button
                    type='button'
                    variant='action'
                    className='price-cta mt-7 group'
                    onClick={() =>
                      void openCheckout(
                        RADAR_PACK_OPTIONS[pack].product,
                        'pricing_page'
                      )
                    }
                  >
                    Choose audit pack
                    <span
                      className='transition-transform duration-200 group-hover:translate-x-[3px]'
                      aria-hidden='true'
                    >
                      →
                    </span>
                  </Button>
                  <ul className='price-features'>
                    {RADAR_PACK_FEATURES.map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </LenisFadeIn>

              <LenisFadeIn delay={160} revealOnScroll className='h-full'>
                <div className='price-card'>
                  <p className='price-plan'>{RADAR_RETAINER_TIER.plan}</p>
                  <p className='price-amount'>
                    <strong>{RADAR_RETAINER_TIER.price}</strong>
                    <span>{RADAR_RETAINER_TIER.unit}</span>
                  </p>
                  <p className='price-note'>{RADAR_RETAINER_TIER.note}</p>
                  <Button
                    type='button'
                    variant='neutral'
                    className='price-cta group'
                    onClick={() =>
                      void openCheckout('retainer', 'pricing_page')
                    }
                  >
                    Start Pro
                    <span
                      className='transition-transform duration-200 group-hover:translate-x-[3px]'
                      aria-hidden='true'
                    >
                      →
                    </span>
                  </Button>
                  <ul className='price-features'>
                    {RADAR_RETAINER_TIER.features.map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </LenisFadeIn>
            </div>
            <div className='flex flex-col items-center gap-2 mt-8'>
              <PoweredByPaddle variant='inline' />
              <p className='text-xs text-muted-foreground m-0'>
                Secure checkout through{' '}
                <strong className='text-foreground/80'>Paddle</strong> · Credits
                never expire · Refund requests accepted within 14 days
              </p>
            </div>
          </Section>
        </div>

        {/* ── How it works */}
        <Section width='wide' id='how-it-works'>
          <div className='grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start'>
            {/* Sticky must live on the grid child itself (the LenisFadeIn
                wrapper) — inside it there is no scroll room to travel. */}
            <LenisFadeIn revealOnScroll className='lg:sticky lg:top-28'>
              <div>
                <Kicker>From signal to fix</Kicker>
                <SectionHeading>
                  Monitoring tells you <em>what</em> AI says. Radar fixes{' '}
                  <em>why.</em>
                </SectionHeading>
                <p className='text-muted-foreground leading-relaxed m-0'>
                  One audit connects the whole chain, from crawler access and
                  structured data to live citations and hallucinations, so you
                  know what to change next.
                </p>
              </div>
            </LenisFadeIn>
            <div className='flex flex-col gap-4'>
              {WORKFLOW.map((item, i) => (
                <LenisFadeIn key={item.step} delay={i * 80} revealOnScroll>
                  <Card className='flex flex-col gap-3 transition-[transform,border-color] duration-200 hover:translate-x-[5px] hover:border-signal/50'>
                    <div className='flex items-baseline gap-4'>
                      <span className='instrument-label text-primary'>
                        {item.step}
                      </span>
                      <h3 className='card-headline card-headline--xl'>
                        {item.title}
                      </h3>
                    </div>
                    <p className='text-sm text-muted-foreground leading-relaxed m-0'>
                      {item.copy}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {item.tags.map(tag => (
                        <span
                          key={tag}
                          className='font-mono text-2xs uppercase tracking-kicker text-muted-foreground border border-line-strong px-2 py-1'
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Card>
                </LenisFadeIn>
              ))}
            </div>
          </div>
        </Section>

        {/* ── 13 tools */}
        <Section width='wide' id='tools'>
          <LenisFadeIn revealOnScroll>
            <div className='max-w-3xl'>
              <Kicker>Breadth</Kicker>
              <SectionHeading>
                Every reason AI gets you wrong, in one audit.
              </SectionHeading>
              <p className='text-muted-foreground leading-relaxed m-0'>
                Start with six technical readiness checks for free. Unlock seven
                live AI-response checks whenever you need the full picture.
              </p>
            </div>
          </LenisFadeIn>

          <LenisFadeIn delay={100} revealOnScroll>
            <div className='mt-10 border border-line'>
              <div className='flex flex-wrap items-center justify-between gap-4 p-5 md:px-6 border-b border-line'>
                <p className='text-sm text-muted-foreground m-0'>
                  <strong className='text-foreground'>13 tools.</strong> One
                  connected diagnosis.
                </p>
                <div
                  className='flex gap-1'
                  role='group'
                  aria-label='Filter tools'
                >
                  {(
                    [
                      { key: 'all', label: 'All 13' },
                      { key: 'free', label: '6 free' },
                      { key: 'paid', label: '7 unlocked' },
                    ] as const
                  ).map(f => (
                    <button
                      key={f.key}
                      type='button'
                      aria-pressed={toolFilter === f.key}
                      onClick={() => setToolFilter(f.key)}
                      className={`font-mono text-2xs uppercase tracking-kicker px-3 py-1.5 border transition-colors ${
                        toolFilter === f.key
                          ? 'bg-[var(--cta-fill)] border-[var(--cta-fill)] text-white'
                          : 'border-line-strong text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line'>
                {visibleTools.map(tool => (
                  <article
                    key={tool.name}
                    className='bg-background p-5 md:p-6 flex flex-col gap-2'
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <span
                        className={`font-mono text-2xs uppercase tracking-kicker ${
                          tool.tier === 'free' ? 'text-growth' : 'text-primary'
                        }`}
                      >
                        {tool.tier === 'free' ? 'Free' : 'Unlocked'}
                      </span>
                      <span
                        className={`inline-block w-1.5 h-1.5 shrink-0 ${
                          tool.tier === 'free' ? 'bg-growth' : 'bg-primary'
                        }`}
                        aria-hidden='true'
                      />
                    </div>
                    <h3 className='card-headline card-headline--base'>
                      {tool.name}
                    </h3>
                    <p className='text-sm text-muted-foreground leading-relaxed m-0'>
                      {tool.desc}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </LenisFadeIn>
        </Section>

        {/* ── Depth: score to action */}
        <Section width='wide'>
          <div className='grid lg:grid-cols-[1fr_1.3fr] gap-10 lg:gap-16 items-start'>
            <LenisFadeIn revealOnScroll className='lg:sticky lg:top-28'>
              <div>
                <Kicker>Depth</Kicker>
                <SectionHeading>
                  A score is only useful when it tells you what to do.
                </SectionHeading>
                <p className='text-muted-foreground leading-relaxed m-0 mb-7'>
                  Radar turns findings into ordered implementation threads and
                  pre-written AI prompts grounded in your own audit data.
                </p>
                {/* Two answers to "can I trust this?", in order of force: a
                    whole audit you can read end to end, then the scoring rules
                    behind it — the same pairing the sample report closes with. */}
                <div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
                  <LinkButton
                    href='/platform/sample-report'
                    variant='neutral'
                    className='group'
                  >
                    View sample report
                    <span
                      className='transition-transform duration-200 group-hover:translate-x-[3px]'
                      aria-hidden='true'
                    >
                      →
                    </span>
                  </LinkButton>
                  <Link
                    href='/platform/methodology'
                    className='group instrument-label text-muted-foreground transition-colors hover:text-foreground'
                  >
                    See how it is scored
                    <span
                      className='inline-block ml-1.5 transition-transform duration-200 group-hover:translate-x-[3px]'
                      aria-hidden='true'
                    >
                      →
                    </span>
                  </Link>
                </div>
              </div>
            </LenisFadeIn>
            <LenisFadeIn delay={120} revealOnScroll>
              {/* Sample dashboard shell per the mock (illustrative
                  demonstration content, not live data). */}
              <div
                className='border border-line bg-card'
                aria-label='Sample Radar dashboard'
              >
                <div className='flex items-center justify-between gap-4 px-4 py-2.5 border-b border-line'>
                  <span className='flex gap-1.5' aria-hidden='true'>
                    <i className='w-2 h-2 rounded-full bg-border' />
                    <i className='w-2 h-2 rounded-full bg-border' />
                    <i className='w-2 h-2 rounded-full bg-border' />
                  </span>
                  <span className='font-mono text-2xs text-muted-foreground'>
                    {DEMO_DOMAIN} · audit complete
                  </span>
                  <span className='font-mono text-2xs text-muted-foreground'>
                    {DEMO_ELAPSED}
                  </span>
                </div>
                <div className='grid sm:grid-cols-[10rem_1fr]'>
                  <aside className='hidden sm:flex flex-col gap-1 p-4 border-r border-line'>
                    <span className='instrument-label text-muted-foreground mb-2'>
                      Audit workspace
                    </span>
                    {[
                      'Overview',
                      `${DEMO_TOOL_COUNT} tools`,
                      'Action plan',
                      'Fix threads',
                      'Competitors',
                    ].map((link, i) => (
                      <span
                        key={link}
                        className={`text-xs px-2 py-1.5 ${
                          i === 0
                            ? 'bg-muted/60 text-foreground font-semibold'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {link}
                      </span>
                    ))}
                  </aside>
                  <div className='p-4 md:p-5'>
                    <div className='flex items-start justify-between gap-4 mb-4'>
                      <div>
                        <p className='instrument-label text-muted-foreground m-0 mb-1.5'>
                          AI readiness score
                        </p>
                        <h3 className='card-headline card-headline--xl'>
                          Clear signal. Ranked fixes.
                        </h3>
                      </div>
                      <span
                        className='font-mono text-xs shrink-0 border px-2.5 py-1'
                        style={{
                          color: GRADE_COLORS[DEMO_GRADE],
                          borderColor: GRADE_COLORS[DEMO_GRADE],
                        }}
                      >
                        {DEMO_GRADE} · {DEMO_SCORE}/100
                      </span>
                    </div>
                    {/* Every tile derived from the canonical dataset. The
                        hand-typed trio (6/13, 9, 2) described a weaker audit
                        than the score beside it, and a different audit again
                        from the homepage dashboard. */}
                    <div className='grid grid-cols-3 gap-px bg-line mb-4'>
                      {[
                        {
                          label: 'Checks passed',
                          value: `${DEMO_PASSING_COUNT}/${DEMO_TOOL_COUNT}`,
                        },
                        { label: 'Open issues', value: `${DEMO_OPEN_ISSUES}` },
                        {
                          label: 'High impact',
                          value: `${DEMO_HIGH_IMPACT_COUNT}`,
                        },
                      ].map(m => (
                        <div key={m.label} className='bg-card p-3'>
                          <p className='text-2xs text-muted-foreground m-0'>
                            {m.label}
                          </p>
                          <p className='font-display font-bold text-lg text-foreground m-0 mt-1'>
                            {m.value}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className='border border-line'>
                      <div className='flex items-center justify-between gap-4 px-4 py-2.5 border-b border-line'>
                        <span className='instrument-label text-muted-foreground'>
                          Priority action plan
                        </span>
                        <span className='instrument-label text-muted-foreground'>
                          {DEMO_RANKED_ACTIONS.length} open
                        </span>
                      </div>
                      {/* The dataset's own actions, in the order the product
                          ranks them, so this list and the homepage's "next
                          best move" can never headline different work. One of
                          the three hand-typed rows named a finding no Radar
                          surface produced. */}
                      {DEMO_RANKED_ACTIONS.map((action, i) => (
                        <div
                          key={action.id}
                          className={`grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 px-4 py-2.5 ${
                            i > 0 ? 'border-t border-line' : ''
                          }`}
                        >
                          <span
                            className={`font-mono text-2xs font-bold ${
                              ACTION_IMPACT[action.impact].color
                            }`}
                          >
                            {ACTION_IMPACT[action.impact].label}
                          </span>
                          <span className='text-xs text-foreground/85'>
                            {action.title}
                          </span>
                          <span className='text-xs text-primary'>
                            Generate fix →
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </LenisFadeIn>
          </div>
        </Section>

        {/* ── Proof story */}
        <Section width='wide' id='proof'>
          <div className='border border-line p-6 md:p-10'>
            <div className='grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center'>
              <LenisFadeIn revealOnScroll>
                <div>
                  <Kicker>Built from real experience</Kicker>
                  <SectionHeading>
                    We were invisible to AI. So we built the tool that fixed it.
                  </SectionHeading>
                  <blockquote className='text-lg text-foreground/85 leading-relaxed m-0 font-display'>
                    &ldquo;Every Radar feature maps to a problem Pixelmojo
                    found, and solved, on its own journey from zero citations to
                    four out of four.&rdquo;
                  </blockquote>
                </div>
              </LenisFadeIn>
              <LenisFadeIn delay={120} revealOnScroll>
                <div className='flex flex-col gap-3'>
                  {[
                    { when: 'Oct ’25', width: 4, score: '0/4' },
                    { when: 'Dec ’25', width: 26, score: '1/4' },
                    { when: 'Jan ’26', width: 52, score: '2/4' },
                    { when: 'Mar ’26', width: 100, score: '4/4' },
                  ].map(row => (
                    <div
                      key={row.when}
                      className='grid grid-cols-[4rem_1fr_2.5rem] items-center gap-3'
                    >
                      <span className='font-mono text-xs text-muted-foreground'>
                        {row.when}
                      </span>
                      <span className='force-circle h-3 bg-muted/60 overflow-hidden'>
                        <span
                          className='force-circle block h-full'
                          style={{
                            width: `${row.width}%`,
                            background:
                              'linear-gradient(90deg, var(--signal), var(--accent))',
                          }}
                        />
                      </span>
                      <span className='font-mono text-xs text-foreground/80 text-right'>
                        {row.score}
                      </span>
                    </div>
                  ))}
                  <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-3'>
                    Six months of hands-on fixes, packaged into a repeatable
                    audit and implementation system.
                  </p>
                </div>
              </LenisFadeIn>
            </div>
          </div>
        </Section>

        {/* ── FAQ */}
        <Section width='wide' id='faq'>
          <div className='grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start'>
            <LenisFadeIn revealOnScroll>
              <div>
                <Kicker>FAQ</Kicker>
                <SectionHeading>
                  Good questions before a first sweep.
                </SectionHeading>
                <p className='text-muted-foreground leading-relaxed m-0'>
                  Short answers about coverage, credits, and how live AI results
                  work.
                </p>
              </div>
            </LenisFadeIn>
            <LenisFadeIn delay={100} revealOnScroll>
              <div className='flex flex-col gap-3'>
                {FAQS.map(faq => (
                  <details
                    key={faq.q}
                    className='group border border-line bg-muted/30'
                  >
                    <summary className='flex items-center justify-between cursor-pointer p-5 md:px-6 text-sm font-semibold text-foreground list-none gap-4'>
                      {faq.q}
                      <span
                        className='font-mono text-muted-foreground transition-transform group-open:rotate-45'
                        aria-hidden='true'
                      >
                        +
                      </span>
                    </summary>
                    <p className='px-5 md:px-6 pb-5 text-sm text-muted-foreground leading-relaxed m-0'>
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </LenisFadeIn>
          </div>
        </Section>

        {/* ── Final CTA. Also the destination of the Free plan's CTA and of
            the hero's pricing anchor: this band holds the page's only
            <FreeCheckLauncher />. */}
        <Section width='wide' id='free-audit'>
          <LenisFadeIn revealOnScroll>
            {/* Mock's cta-shell: concentric ripple rings behind a center
                glow, on the primary tint. */}
            <div
              className='border border-primary/30 p-8 md:p-16 text-center overflow-hidden'
              style={{
                backgroundImage: [
                  'radial-gradient(circle at center, color-mix(in srgb, var(--primary) 14%, transparent), transparent 38%)',
                  'repeating-radial-gradient(circle at center, transparent 0 64px, color-mix(in srgb, var(--primary) 7%, transparent) 65px 66px, transparent 67px 130px)',
                ].join(', '),
              }}
            >
              <InstrumentLabel className='justify-center mb-5'>
                Your first sweep is free
              </InstrumentLabel>
              <h2 className='thesis-headline text-center'>
                See why AI isn&rsquo;t recommending you.
              </h2>
              <p className='text-muted-foreground leading-relaxed max-w-2xl mx-auto mt-5 mb-2'>
                Run six technical readiness checks free. If you need the full
                answer, unlock all 13 tools and every exact-fix prompt from $5.
              </p>
              {/* The real free-check flow: domain + email -> verification
                  code -> the audit runs server-side -> the user lands on
                  /platform/check/<id> with the result. Same component and
                  same gate as the hero instance, per /platform/start's
                  repeat-CTA pattern. */}
              <FreeCheckLauncher />
            </div>
          </LenisFadeIn>
        </Section>
      </div>
    </>
  )
}
